import React, { FunctionComponent, ReactNode, memo } from 'react';
import styled from '@emotion/styled';
import { useHistory } from 'react-router';
import Icon from '~ComponentsImproved/Icon';
import BackIcon from '~Assets/icons/arrow_back.svg';
import { useButton } from '~Hooks';
import { handyBreakpoint } from '~Styles/breakpoints';

export const Base = styled.div<{ dark: boolean }>`
  display: flex;
  padding: 24px 8px 8px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.14);
  background-color: ${({ dark }) => (dark ? 'var(--colorBlueGray1200)' : 'var(--colorWhite)')};
  position: relative;
`;

export const BackButton = styled.div<{
  touching?: boolean;
  dark?: boolean;
  hideOnHandy?: boolean;
}>`
  width: 50px;
  height: 32px;
  cursor: pointer;
  background-color: ${({ dark }) => (dark ? 'transparent' : 'var(--colorBlueGray200)')};
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
  transition: all 0.2s ease-out;
  transform: ${({ touching }) => (touching ? 'scale(0.96)' : '1')};

  /* On handheld width the host shell (e.g. the Handy TopBar) already provides back navigation. Hiding it here also frees the row */
  ${({ hideOnHandy }) => (hideOnHandy ? `${handyBreakpoint} { display: none; }` : '')}
`;

export const Title = styled.h1<{ dark: boolean }>`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  margin-right: auto;
  color: ${({ dark }) => (dark ? 'var(--colorWhite)' : 'var(--colorBlueGray1200)')};

  /* On narrow (Handy) screens a long title would crowd out the action buttons,
     so shrink it and truncate with an ellipsis instead of pushing them off-row. */
  ${handyBreakpoint} {
    font-size: 16px;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

interface Top {
  title: string;
  goBack?: () => void;
  dark?: boolean;
  children?: ReactNode;
  hideBackButtonOnHandy?: boolean; //hide on handy
}

const BackTopBar: FunctionComponent<Top> = ({
  title,
  goBack,
  dark = false,
  children,
  hideBackButtonOnHandy = false,
}) => {
  const history = useHistory();

  const handleBack = () => {
    if (goBack) {
      goBack();
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
    } else {
      history.push('gateway');
    }
  };

  const backButton = useButton({
    onClick: handleBack,
  });

  return (
    <Base dark={dark}>
      <BackButton {...backButton} dark={dark} hideOnHandy={hideBackButtonOnHandy}>
        <Icon src={BackIcon} color={dark ? 'var(--colorWhite)' : 'var(--colorBlueGray1200)'} />
      </BackButton>
      <Title dark={dark}>{title}</Title>
      {children && <div>{children}</div>}
    </Base>
  );
};

/** Memoized so the bar only re-renders when one of its props (title, goBack, dark, children) changes */
export default memo(BackTopBar);
